
  import {
    ComponentPublicInstance,
    computed,
    defineComponent,
    inject,
    PropType,
    ref,
    shallowRef,
    watchEffect,
  } from 'vue';
  import { Cell, Column, FlexRender, getCoreRowModel, Header, Row, Table, useVueTable } from '@tanstack/vue-table';
  import { useVirtualizer } from '@tanstack/vue-virtual';

  import LoaderInfinityMore from '@/common/components/redesigned/LoaderInfinityMore.vue';
  import { DEFAULT_PAGINATION_PAGE } from '@/common/constants/page';
  import { TApiResponse } from '@/common/types/response';
  import { TStorePaths } from '@/common/types/storePaths';

  import Loader from '@/components/Loader.vue';

  import { AGGREGATED_TABLE_PDF_EXPORT_VIRTUALIZATION_KEY } from './constants/exportInjection';
  import TableAggregatedProgress from './TableAggregatedProgress.vue';
  import {
    TCachedVirtualRows,
    TFootRenderParams,
    TFootTableItem,
    TPaginationConfig,
    TTableAggregatedProgressBarOptions,
    TTableData,
    TTableWithOuterFilterConfig,
  } from './types/tableTypes';
  import { PaginationFilter } from './utils/filters';
  import { normalizeItemsTextSpacing } from './utils/normilizeTextSpacing';
  import type { TCellSpanCache, TCellsSpanCache } from './utils/tableAggregatedCellSpanCache';
  import {
    buildCellsSpanCacheForRows,
    buildCellsSpanVisibilityCacheForRowRange,
    getCellSpanCacheInitValue,
    getCellSpanCacheKey,
    getSpanVisibilityRowRange,
  } from './utils/tableAggregatedCellSpanCache';
  import {
    cancelPendingTableDataPreparation,
    prepareTableDataInWorker,
    shouldUseTableAggregatedWorker,
    terminateTableAggregatedWorker,
  } from './utils/tableAggregatedPrepareWorker';

  import { TPageMetadata } from '@/types/meta';

  type TLoadStrategy = 'default' | 'infiniteScroll' | 'progressive';

  type TPaginationData = TPageMetadata & { items: TTableData[] };

  export default defineComponent({
    name: 'TableAggregated',
    components: {
      LoaderInfinityMore,
      TableAggregatedProgress,
      FlexRender,
      Loader,
    },
    inheritAttrs: false,
    props: {
      generateColumns: {
        type: Function,
        required: true,
      },
      rowClassGetter: {
        type: Function,
      },
      cellClassGetter: {
        type: Function,
      },
      filterParams: {
        type: Object as PropType<TTableWithOuterFilterConfig['params']>,
        required: true,
      },
      pagination: {
        type: Object as PropType<TPaginationConfig>,
        default: () => ({
          size: 20,
        }),
      },
      storePaths: {
        type: Object as PropType<TStorePaths>,
        required: true,
      },
      isArrowNav: {
        type: Boolean,
        default: false,
      },
      loadStrategy: {
        type: String as PropType<TLoadStrategy>,
        validator: (value: TLoadStrategy) => ['default', 'infiniteScroll', 'progressive'].includes(value),
        default: 'default',
      },
      progressBarOptions: {
        type: Object as PropType<TTableAggregatedProgressBarOptions>,
      },
      footerRenderer: {
        type: Function as PropType<(params: TFootRenderParams<any>) => TFootTableItem[]>,
        default: null,
      },
      isInitLoadData: {
        type: Boolean,
        default: true,
      },
      notFoundMode: {
        type: String,
        validator: (value: string) => ['cell', 'outside'].includes(value),
        default: 'cell',
      },
      emptyText: {
        type: String,
        default: 'Нет данных',
      },
      widthStrategy: {
        type: String,
        validator: (value: string) => ['maxContent', 'full'].includes(value),
        default: 'maxContent',
      },
    },
    data(): {
      table: Table<unknown> | null;
      isLoading: boolean;
      isLoadingMore: boolean;
      cacheHeaderRowSpan: Set<string>;
      paginationFilter: PaginationFilter;
      widthTable: string;
      loaderWidth: string;
      isStartInitLoadData: boolean;
      lastNonExportScrollTop: number | null;
      cellsSpanCache: TCellsSpanCache;
      cellsSpanCachedRowCount: number;
      cellsSpanVisibilityCachedFrom: number;
      cellsSpanVisibilityCachedTo: number;
      abortController: AbortController | null;
      isPreparing: boolean;
      prepareRequestId: number;
    } {
      return {
        table: null,
        isLoading: false,
        isLoadingMore: false,
        isPreparing: false,
        prepareRequestId: 0,
        cacheHeaderRowSpan: new Set(),
        paginationFilter: new PaginationFilter({ name: 'filter' }),
        widthTable: '',
        loaderWidth: '100%',
        isStartInitLoadData: false,
        lastNonExportScrollTop: null,
        cellsSpanCache: getCellSpanCacheInitValue(),
        cellsSpanCachedRowCount: 0,
        cellsSpanVisibilityCachedFrom: -1,
        cellsSpanVisibilityCachedTo: -1,
        abortController: null,
      };
    },
    created() {
      this.resetData();
      this.setPaginationFilter(this.getMeta.pageNumber);

      if (this.isInitLoadData) {
        this.fetchData();
      }
    },
    mounted() {
      this.syncTableLayoutMetrics();
      window.addEventListener('resize', this.syncTableLayoutMetrics);
    },
    beforeUnmount() {
      window.removeEventListener('resize', this.syncTableLayoutMetrics);
      cancelPendingTableDataPreparation();
      terminateTableAggregatedWorker();
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    },
    setup() {
      const tablePdfExportVirtualizationDisabled = inject(AGGREGATED_TABLE_PDF_EXPORT_VIRTUALIZATION_KEY);

      const virtualizerCount = ref(0);
      const scrollElement = ref<HTMLElement | null>(null);

      const cachedVirtualRows = shallowRef<TCachedVirtualRows>([]);
      const cachedTotalSize = ref('0px');

      const rowVirtualizerOptions = computed(() => ({
        count: virtualizerCount.value,
        getScrollElement: () => scrollElement.value,
        estimateSize: () => 55,
        overscan: 4,
        useAnimationFrameWithResizeObserver: true,
      }));

      const rowVirtualizer = useVirtualizer(rowVirtualizerOptions);

      watchEffect(
        () => {
          if (rowVirtualizer.value) {
            const items = rowVirtualizer.value.getVirtualItems();
            cachedVirtualRows.value = items ?? [];

            const size = rowVirtualizer.value.getTotalSize();
            cachedTotalSize.value = size !== undefined ? `${size}px` : '0px';
          }
        },
        { flush: 'post' },
      );

      return {
        tablePdfExportVirtualizationDisabled,
        rowVirtualizer,
        virtualizerCount,
        scrollElement,
        cachedVirtualRows,
        cachedTotalSize,
      };
    },
    watch: {
      tablePdfExportVirtualizationDisabled(newVal: boolean) {
        const tableWrapper = this.$refs.tableWrapper as HTMLElement | undefined;
        if (!tableWrapper) {
          if (newVal) {
            this.buildFullSpanVisibilityCache();
          }
          return;
        }

        if (newVal) {
          if (this.lastNonExportScrollTop === null) {
            this.lastNonExportScrollTop = tableWrapper.scrollTop;
          }
          this.buildFullSpanVisibilityCache();
          return;
        }

        if (this.lastNonExportScrollTop !== null) {
          const restoreTop = this.lastNonExportScrollTop;
          this.lastNonExportScrollTop = null;

          this.$nextTick(() => {
            requestAnimationFrame(() => {
              tableWrapper.scrollTop = restoreTop;
            });
          });
        }
      },
      getData: {
        handler(newVal) {
          this.$nextTick(() => {
            if (this.$refs.tableWrapper) {
              this.setVirtualizerOptions(newVal.items?.length ?? 0, this.$refs.tableWrapper as HTMLElement);
              this.syncTableLayoutMetrics();
            }
          });

          this.table = useVueTable({
            data: newVal.items,
            columns: this.generateColumns(newVal),
            getCoreRowModel: getCoreRowModel(),
          });
        },
      },
      getRows: {
        handler(rows: Row<unknown>[]) {
          if (!rows.length || !this.table) {
            this.cellsSpanCache = getCellSpanCacheInitValue();
            this.cellsSpanCachedRowCount = 0;
            this.cellsSpanVisibilityCachedFrom = -1;
            this.cellsSpanVisibilityCachedTo = -1;
            return;
          }

          const { cellsSpanCache, cellsSpanCachedRowCount } = buildCellsSpanCacheForRows({
            rows,
            isPaginationLoadStrategy: this.getIsPaginationLoadStrategy,
            prevCachedRowCount: this.cellsSpanCachedRowCount,
            prevCellsSpanCache: this.cellsSpanCache,
          });

          this.cellsSpanCache = cellsSpanCache;
          this.cellsSpanCachedRowCount = cellsSpanCachedRowCount;
          this.cellsSpanVisibilityCachedFrom = -1;
          this.cellsSpanVisibilityCachedTo = -1;
          this.refreshSpanVisibilityCache();
        },
        immediate: true,
      },
      cachedVirtualRows: {
        handler() {
          this.refreshSpanVisibilityCache();
        },
        deep: true,
      },
      filterParams: {
        handler() {
          this.setPaginationFilter(1);
          this.fetchData();
          (this.$refs.tableWrapper as HTMLElement | undefined)?.scrollTo({ top: 0, behavior: 'smooth' });
        },
        deep: true,
      },
      isLoading() {
        this.syncTableLayoutMetrics();
      },
    },
    methods: {
      async fetchData(isMore = false) {
        if (this.getIsProgressiveLoadStrategy && !isMore) {
          this.setPaginationFilter(1);
        }

        if (this.abortController) {
          this.abortController.abort();
        }
        cancelPendingTableDataPreparation();
        this.prepareRequestId += 1;
        this.abortController = new AbortController();
        const currentController = this.abortController;

        this.isStartInitLoadData = true;
        this.setLoadingMore(true, isMore);
        const allParams: TTableWithOuterFilterConfig['params'] = { ...this.filterParams };

        if (this.getIsPaginationLoadStrategy) {
          [this.paginationFilter].forEach((filter) => {
            const entries = filter.toEntries();
            if (entries) {
              entries.forEach(([key, value]) => {
                allParams[key] = value;
              });
            }
          });
        }

        try {
          const response: TApiResponse<TPaginationData | TTableData[]> = await this.searchData(allParams);

          if (this.abortController !== currentController) {
            return;
          }

          if (response.success) {
            const data = response.data;
            const preparedData: TPaginationData = Array.isArray(data)
              ? {
                  ...DEFAULT_PAGINATION_PAGE,
                  items: data,
                }
              : data;
            await this.setData(preparedData, isMore);

            if (this.getIsProgressiveLoadStrategy && this.getMeta.pageNumber < this.getMeta.pagesTotal) {
              if (!isMore) {
                this.isLoading = false;
              }

              this.setPaginationFilter(this.getMeta.pageNumber + 1);
              await this.fetchData(true);
            }
          }
        } catch (error) {
          console.error(error);
        } finally {
          if (this.abortController === currentController) {
            this.setLoadingMore(false, isMore);
          }

          await this.$nextTick();
          this.syncTableLayoutMetrics();
        }
      },
      setLoadingMore(value: boolean, isMore: boolean) {
        if (isMore) {
          this.isLoadingMore = value;
        } else {
          this.isLoading = value;
        }
      },
      // TODO: отрефакторить для снижения когнитивной сложности метода
      async setData(data: TPaginationData, isMore: boolean) {
        if (!isMore) {
          this.cellsSpanCachedRowCount = 0;
        }

        let result: TPaginationData = { ...data, items: data.items };
        const oldValue = isMore ? this.$store.getters[this.storePaths.getter] : null;
        const prevItems = oldValue?.items ?? [];

        if (isMore) {
          result = {
            ...result,
            firstItemNumber: oldValue?.firstItemNumber ?? 1,
            pagesTotal: data?.pagesTotal === -1 ? oldValue.pagesTotal : data?.pagesTotal ?? 1,
            itemsTotal: data?.itemsTotal === -1 ? oldValue.itemsTotal : data?.itemsTotal ?? 1,
          };
        }
        // ? Возможно метод normilize стоит удалить, т.к. при больших объемов сильно тормозит браузер
        const shouldUseWorker = shouldUseTableAggregatedWorker(result.items.length, isMore, prevItems.length);

        if (shouldUseWorker) {
          const requestId = ++this.prepareRequestId;
          this.isPreparing = true;

          try {
            const prepared = await prepareTableDataInWorker({
              items: result.items,
              isMore,
              prevItems,
            });

            if (requestId !== this.prepareRequestId) {
              return;
            }

            result = {
              ...result,
              items: prepared.items as TTableData[],
            };
          } catch (error) {
            if (requestId !== this.prepareRequestId) {
              return;
            }

            console.error(error);
            result = {
              ...result,
              items: normalizeItemsTextSpacing(isMore ? [...prevItems, ...result.items] : result.items) as TTableData[],
            };
          } finally {
            if (requestId === this.prepareRequestId) {
              this.isPreparing = false;
            }
          }
        } else {
          result = {
            ...result,
            items: normalizeItemsTextSpacing(isMore ? [...prevItems, ...result.items] : result.items) as TTableData[],
          };
        }

        this.$store.commit(this.storePaths.mutation, { ...result });
      },
      resetData() {
        this.setData(
          {
            ...this.$store.getters[this.storePaths.getter],
            ...DEFAULT_PAGINATION_PAGE,
          },
          false,
        );
      },
      searchData(params: TTableWithOuterFilterConfig['params']) {
        return this.$store.dispatch(this.storePaths.action, { params, abortController: this.abortController });
      },
      calculateHeaderRowSpan(header: Header<unknown, unknown>) {
        if (this.cacheHeaderRowSpan.has(header.column.id)) return 0;

        if (header.depth === 1 && header.isPlaceholder) {
          this.cacheHeaderRowSpan.add(header.column.id);
          return this.table?.getHeaderGroups().length;
        }

        return 1;
      },
      getRowspanValue(cell: Cell<unknown, unknown>): number | null | undefined {
        const key = getCellSpanCacheKey(cell.row.index, cell.column.id);
        return this.getRowspanCache.get(key);
      },
      getIsRowspan(cell: Cell<unknown, unknown>): boolean {
        const rowspan = this.getRowspanValue(cell);
        return rowspan != null && rowspan > 1;
      },
      getIsLastRowspan(cell: Cell<unknown, unknown>): boolean {
        const key = getCellSpanCacheKey(cell.row.index, cell.column.id);
        return this.cellsSpanCache.rowspanLastInSpan.get(key) ?? false;
      },
      getIsHideCell(cell: Cell<unknown, unknown>): boolean {
        const key = getCellSpanCacheKey(cell.row.index, cell.column.id);
        return this.cellsSpanCache.rowspanHideCell.get(key) ?? false;
      },
      getColspanValue(cell: Cell<unknown, unknown>): number | null | undefined {
        const key = getCellSpanCacheKey(cell.row.index, cell.column.id);
        return this.getColspanCache.get(key);
      },
      getIsColspan(cell: Cell<unknown, unknown>): boolean {
        const colspan = this.getColspanValue(cell);
        return colspan != null && colspan > 1;
      },
      getIsLastColspan(cell: Cell<unknown, unknown>): boolean {
        const key = getCellSpanCacheKey(cell.row.index, cell.column.id);
        return this.cellsSpanCache.colspanLastInSpan.get(key) ?? false;
      },
      getIsHideCellForColspan(cell: Cell<unknown, unknown>): boolean {
        const key = getCellSpanCacheKey(cell.row.index, cell.column.id);
        return this.cellsSpanCache.colspanHideCell.get(key) ?? false;
      },
      isShowHeaderCell(header: Header<unknown, unknown>) {
        return (
          !this.cacheHeaderRowSpan.has(header.column.id) ||
          (this.cacheHeaderRowSpan.has(header.column.id) && header.depth === 1)
        );
      },
      getHeaderMetaClass(header: Header<unknown, unknown>): string | undefined {
        return header.column.columnDef.meta?.class;
      },
      getIsCellFixed(cell: Cell<unknown, unknown> | Header<unknown, unknown>): boolean {
        return cell.column.columnDef.meta?.fixed === true;
      },
      getIsLastFixedColumn(column: Column<unknown>): boolean {
        if (!this.table || column.columnDef.meta?.fixed !== true) return false;

        const fixedColumns = this.table.getAllColumns().filter((col) => col.columnDef.meta?.fixed === true);

        if (!fixedColumns.length) return false;

        return fixedColumns[fixedColumns.length - 1].id === column.id;
      },
      getCellMetaClass(cell: Cell<unknown, unknown>): string | undefined {
        return cell.column.columnDef.meta?.class;
      },
      getFixedLeft(item: Cell<unknown, unknown> | Header<unknown, unknown>) {
        return this.getIsCellFixed(item) ? { left: `${this.calculateFixedLeft(item.column)}px` } : {};
      },
      shouldRestrictCellWidthForPdf(): boolean {
        return Boolean(this.tablePdfExportVirtualizationDisabled && this.widthStrategy === 'maxContent');
      },
      getColumnSizeStyle(column: Column<unknown>) {
        const size = `${column.getSize()}px`;
        return {
          minWidth: size,
          maxWidth: this.shouldRestrictCellWidthForPdf() ? size : undefined,
        };
      },
      getHeaderStyle(header: Header<unknown, unknown>) {
        return {
          ...this.getColumnSizeStyle(header.column),
          ...this.getFixedLeft(header),
        };
      },
      getCellStyle(cell: Cell<unknown, unknown>) {
        return {
          ...this.getColumnSizeStyle(cell.column),
          ...this.getFixedLeft(cell),
          ...this.getTextAlignStyle(cell),
        };
      },
      calculateFixedLeft(column: Column<unknown>) {
        if (!this.table) return 0;

        const allColumns = this.table.getAllColumns();
        const currentColumnIndex = allColumns.findIndex((col) => col.id === column.id);

        if (currentColumnIndex === -1) return 0;

        let left = 0;
        for (let i = 0; i < currentColumnIndex; i++) {
          const col = allColumns[i];
          if (col.columnDef.meta?.fixed) {
            left += col.getSize();
          }
        }

        return left;
      },
      getTextAlignStyle(cell: Cell<unknown, unknown>) {
        const textAlign = cell.column.columnDef.meta?.textAlign;

        if (!textAlign) return {};

        if (typeof textAlign === 'function') return { textAlign: textAlign(cell) };

        return { textAlign };
      },
      setPaginationFilter(page: number) {
        this.paginationFilter.setValue({ size: this.pagination?.size ?? 0, number: page });
      },
      onScroll(event: Event) {
        if (this.isLoading || this.isLoadingMore || !this.getIsInfinityScrollLoadStrategy) return;
        if (this.getMeta.pageNumber === this.getMeta.pagesTotal) return;
        if (!this.getData.items.length) return;

        const target = event.target as HTMLElement;
        const { scrollTop, scrollHeight, clientHeight } = target;

        if (scrollHeight - scrollTop - clientHeight <= 200) {
          this.setPaginationFilter(this.getMeta.pageNumber + 1);
          this.fetchData(true);
        }
      },
      setWidthTable() {
        const tableWrapper = this.$refs.tableWrapper as HTMLElement | undefined;
        if (!tableWrapper) {
          return;
        }
        const left = tableWrapper.getBoundingClientRect().left;
        this.widthTable = `calc(100vw - ${left}px - 20px)`;
      },
      syncTableLayoutMetrics() {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.setWidthTable();
            this.updateLoaderWidth();
          });
        });
      },
      measureElement(el: Element | ComponentPublicInstance | null) {
        if (!el || !(el instanceof HTMLElement)) {
          return;
        }

        this.rowVirtualizer?.measureElement(el);
      },
      setVirtualizerOptions(count: number, element: HTMLElement) {
        this.virtualizerCount = count;
        this.scrollElement = element;
      },
      updateLoaderWidth() {
        const tableWrapper = this.$refs.tableWrapper as HTMLElement | undefined;
        const wrapper = this.$refs.wrapperRef as HTMLElement | undefined;
        const target = tableWrapper ?? wrapper;

        if (!target) {
          return;
        }

        const width = target.clientWidth;
        if (width > 0) {
          this.loaderWidth = `${width}px`;
        }
      },
      getRowClass(row: { index: number }) {
        if (!this.getRows[row.index]?.original) return '';
        return this.rowClassGetter?.(this.getRows[row.index].original);
      },
      buildFullSpanVisibilityCache() {
        const rows = this.getRows;
        if (!rows.length || !this.table) return;

        buildCellsSpanVisibilityCacheForRowRange({
          rows,
          cellsSpanCache: this.cellsSpanCache,
          fromRowIndex: 0,
          toRowIndex: rows.length - 1,
        });

        this.cellsSpanVisibilityCachedFrom = 0;
        this.cellsSpanVisibilityCachedTo = rows.length - 1;
      },
      refreshSpanVisibilityCache() {
        const rows = this.getRows;
        if (!rows.length || !this.table) return;

        if (this.tablePdfExportVirtualizationDisabled) return this.buildFullSpanVisibilityCache();

        const range = getSpanVisibilityRowRange(this.cachedVirtualRows, rows.length);
        if (!range) return;

        const { fromRowIndex, toRowIndex } = range;
        const isRangeCached =
          this.cellsSpanVisibilityCachedFrom !== -1 &&
          fromRowIndex >= this.cellsSpanVisibilityCachedFrom &&
          toRowIndex <= this.cellsSpanVisibilityCachedTo;

        if (isRangeCached) return;

        buildCellsSpanVisibilityCacheForRowRange({
          rows,
          cellsSpanCache: this.cellsSpanCache,
          fromRowIndex,
          toRowIndex,
        });

        this.cellsSpanVisibilityCachedFrom =
          this.cellsSpanVisibilityCachedFrom === -1
            ? fromRowIndex
            : Math.min(this.cellsSpanVisibilityCachedFrom, fromRowIndex);
        this.cellsSpanVisibilityCachedTo =
          this.cellsSpanVisibilityCachedTo === -1 ? toRowIndex : Math.max(this.cellsSpanVisibilityCachedTo, toRowIndex);
      },
    },
    computed: {
      getMeta() {
        const getterDate = this.$store.getters[this.storePaths.getter];
        return {
          pageSize: getterDate?.pageSize ?? this.pagination?.size ?? 0,
          pageNumber: getterDate?.pageNumber ?? 1,
          pagesTotal: getterDate?.pagesTotal ?? 1,
          itemsTotal: getterDate?.itemsTotal ?? 0,
          itemCount: getterDate?.itemCount ?? 0,
          firstItemNumber: getterDate?.firstItemNumber ?? 1,
        };
      },
      getData() {
        return this.$store.getters[this.storePaths.getter];
      },
      getItems() {
        return this.getData.items ?? [];
      },
      getVirtualRows() {
        if (this.tablePdfExportVirtualizationDisabled) {
          const rows = this.getRows;
          return rows.map((_, index) => ({
            index,
            start: 0,
            size: 0,
            end: 0,
          }));
        }
        return this.cachedVirtualRows;
      },
      getTotalSize() {
        if (this.tablePdfExportVirtualizationDisabled) {
          return 'auto';
        }
        return this.cachedTotalSize;
      },
      getRows() {
        return this.table?.getRowModel().rows ?? [];
      },
      getIsGroupMode() {
        return (this.table?.getHeaderGroups().length ?? 0) > 1;
      },
      getFooterParams(): TFootRenderParams<unknown> {
        return { meta: this.getIsPaginationLoadStrategy ? this.getMeta : null, items: this.getItems };
      },
      getIsPaginationLoadStrategy(): boolean {
        return this.loadStrategy !== 'default';
      },
      getIsInfinityScrollLoadStrategy(): boolean {
        return this.loadStrategy === 'infiniteScroll';
      },
      getIsProgressiveLoadStrategy(): boolean {
        return this.loadStrategy === 'progressive';
      },
      getShowIsNotFound() {
        return !this.getItems.length && this.isStartInitLoadData;
      },
      getShowIsNotFoundCell() {
        return this.getShowIsNotFound && this.notFoundMode === 'cell' && !this.isLoading && !this.isPreparing;
      },
      getShowIsNotFoundOutside() {
        return this.getShowIsNotFound && !this.isLoading && !this.isPreparing && this.notFoundMode === 'outside';
      },
      getEmptyCellColspan(): number {
        if (!this.table) return 1;

        const firstHeaderGroup = this.table.getHeaderGroups()[0];
        if (!firstHeaderGroup) return 1;

        return firstHeaderGroup.headers.reduce((sum, header) => {
          if (this.isShowHeaderCell(header)) {
            return sum + (header.colSpan || 1);
          }
          return sum;
        }, 0);
      },
      getRowspanCache(): TCellSpanCache {
        return this.cellsSpanCache.rowspan;
      },
      getColspanCache(): TCellSpanCache {
        return this.cellsSpanCache.colspan;
      },
      getWrapperStyle() {
        return {
          width: this.widthStrategy === 'full' && !this.isLoading ? '100%' : 'max-content',
        };
      },
    },
  });
